import React from 'react';

import { GetServerSidePropsContext } from 'next';
import { dehydrate, HydrateProps, QueryClient } from 'react-query';
import { Pages, TrackingSetup, VWOSetup } from '~/lib/data-contract';
import { DynamicPage, errorHandler, Franchise } from '~/templates/pages';
import { slugDataFetcher, staticDataResolver, handleSSRResponse } from '~/templates/pages/utils';
import { getIsInitialLoad } from '~/templates/pages/utils/isInitialLoad';
import { getHeaders } from '~/templates/pages/utils/dataFetcher';

export type DynamicPageProps = {
    page?: Pages;
    franchise?: Franchise;
    dehydratedState?: HydrateProps['state'];
    settings: {
        referer: string;
        trackingSetup?: TrackingSetup;
        VWOSetup?: VWOSetup;
    };
    pageBaseUrl?: string;
    resolvedUrl?: string;
    locale?: string;
};

const Slug = (props: DynamicPageProps) => {
    return <DynamicPage {...props} />;
};

/**
 * Fetch page, frame and any page specific requests, return as props
 * CacheControl is set based on the response header from the page request
 */
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { req, res, locale = '', resolvedUrl } = context;
    const { host = '' } = req.headers;
    
    const isInitialLoad = getIsInitialLoad(context);
    const queryClient = new QueryClient();
    
    const headers = await getHeaders(req);

    let data;
    try {
        data = await slugDataFetcher({
            headers,
            host,
            locale,
            queryClient,
            resolvedUrl,
        });
    } catch (error) {
        return errorHandler(error, resolvedUrl);
    }

    const { pageResponse, frame, franchise, originalStatus } = data || {};
    const { settings } = staticDataResolver({ franchise, frame, headers, isInitialLoad });

    // Handle SSR response logic (headers, redirects, status codes, cache-control)
    const ssrResult = handleSSRResponse({
        res,
        pageResponse,
        status: originalStatus,
        resolvedUrl,
    });

    if (ssrResult?.redirect) {
        return ssrResult;
    }

    const dehydratedState = dehydrate(queryClient);
    // Clear the cache to avoid high memory consumption on server
    queryClient.clear();

    return {
        props: {
            dehydratedState,
            franchise,
            settings,
            pageBaseUrl: host?.includes('local') ? `http://${host}` : `https://${host}`,
            resolvedUrl,
            page: pageResponse?.data ?? null,
            locale,
        },
    };
};

export default Slug;
